import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ApprovalStatus, Offer, Quote, QuotePriceStatus, QuoteState } from '@app/quoting/models/quote';
import { MoreDetailsComponent } from '@app/shared/components/more-details/more-details.component';
import { ResolveOffersDialogComponent } from '@app/shared/components/resolve-offers-dialog/resolve-offers-dialog.component';
import { ApprovalAccept, OfferApprovalRequest, SetQuote } from '@quoting/state/actions/quoting-module.actions';
import { Store } from '@ngrx/store';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { UserRole, UtilityHubIdentity } from '@auth/models/utility-hub-identity';
import { QuoteRequestService } from '@app/shared/services/quote-request.service';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { QuoteRequestState } from '@app/shared';
import { CommodityType } from '@app/shared/enums/commodity-type';
import { PricingSource, PricingSourceService } from '@app/shared/services/pricing-source.service';
import { QuoteRequestType } from '@app/shared/models/QuoteRequestType';
import LogRocket from 'logrocket';
import { map, take } from 'rxjs/operators';
import { MarginTerm } from '@app/quoting/models/quote-request-summary';
import { AppState } from '@app/state/app.state';
import { contractLengths_Selected, marginDollarSelector, marginTermSelector, productTypeSelector, utilityUOMSelector } from '@utility-information/state/selectors/utility-information.selectors';
import { selectQuote } from '@quoting/state/selectors/quoting-module.selectors';
import { SetMarginTerm } from '@utility-information/state/actions/utility-information.actions';
import { quoteSelector, configurationSelector, isReadOnlySelector } from '@app/state/app.selectors';
import { QuotationDeactivationCheckerService } from '@app/shared/services/quotation-deactivation-checker/quotation-deactivation-checker.service';
import { QuoteExpirationDisclaimerDialogComponent } from '@app/shared/components/quote-expiration-disclaimer-dialog/quote-expiration-disclaimer-dialog.component';
import { QuoteStateHelperService } from '@app/shared/services/quote-state-helper.service';


@Component({
  selector: 'app-offer',
  templateUrl: './offer.component.html',
  styleUrls: ['./offer.component.scss'],
  providers: [QuotationDeactivationCheckerService]
})
export class OfferComponent implements OnInit, OnDestroy, OnChanges {
  @Input() quoteRequestState: QuoteRequestState;
  @Input() inputAnnualUsage: number;
  @Input() offer: Offer;
  @Input() maxPlanTypeLength: number;
  @Input() isAllMarginChanged: boolean;
  @Output() actioned = new EventEmitter<number>();

  public quoteStateType = QuoteState;
  public quoteState: QuoteState;
  public quoteRequestType: QuoteRequestType;
  public quoteRequestOwnerId: string;
  public pricingSource: PricingSource;
  public pricingSourceEnum = PricingSource;
  public quoteRequestTypeEnum = QuoteRequestType;
  marginTermResults = Array<MarginTerm>();
  private quote: Quote;
  user: UtilityHubIdentity;
  selectQuote$: Subscription;
  sendOfferDialog$: Subscription;
  consumptionData: any;
  routeSubscription$: Subscription;
  quoteRequestId: any;
  docuSignEnabled: boolean;
  uhConfig$: Subscription;
  supplierId: any;
  generateDocument$: Subscription;
  loading: boolean;
  QuoteRequestTypeEnum = QuoteRequestType;
  productType$: Subscription;
  utilityUOM$: any;
  marginDollar$: Subscription;
  marginDollarInput$: Subscription;
  marginTerm$: any;
  contractLengths$: any;
  marginDollar: number;
  isAnyMarginEdited: boolean = false;
  marginPreviousInput: number;
  productType: string;
  isReadOnly$: Subscription;
  isReadOnly: boolean;

  constructor(
    private dialog: MatDialog,
    private store: Store<AppState>,
    iconRegistry: MatIconRegistry,
    sanitizer: DomSanitizer,
    private quoteRequestService: QuoteRequestService,
    private route: ActivatedRoute,
    private pricingSourceService: PricingSourceService,
    public quotationDeactivationCheckerService: QuotationDeactivationCheckerService,
    public quoteStateHelperService: QuoteStateHelperService
  ) {
    iconRegistry.addSvgIcon('cancel', sanitizer.bypassSecurityTrustResourceUrl('assets/icons/icon_cancel.svg'));
    iconRegistry.addSvgIcon('check_circle', sanitizer.bypassSecurityTrustResourceUrl('assets/icons/icon_success_no_style.svg'));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.isAllMarginChanged){
      this.isAnyMarginEdited = changes?.isAllMarginChanged.currentValue;
    }
  }

  isGasQuote(): boolean {
    return this.quoteState && this.quoteRequestState.commodity === CommodityType.Gas;
  }

  ngOnDestroy(): void {
    this.selectQuote$?.unsubscribe();
    this.sendOfferDialog$?.unsubscribe();
    this.uhConfig$?.unsubscribe();
    this.generateDocument$?.unsubscribe();
    this.marginDollar$?.unsubscribe();
    this.productType$?.unsubscribe();
    this.routeSubscription$?.unsubscribe();
    this.isReadOnly$?.unsubscribe();
  }

  ngOnInit(): void {
    this.marginTerm$ = this.store.select(marginTermSelector);
    this.utilityUOM$ = this.store.select(utilityUOMSelector);
    // Track the per-term margin from the store for this specific offer term.
    // Using marginTermSelector (not marginDollarSelector) ensures the displayed
    // value always reflects the individual term's current margin in the store,
    // even after the user changes it via setMarginTermDynamic.
    this.marginDollar$ = this.store.select(marginTermSelector).subscribe((terms) => {
      const termEntry = terms?.find(t => t.term === this.offer?.term);
      this.marginDollar = termEntry != null ? termEntry.margin : (this.offer?.margin ?? 0);
    });
    this.selectQuote$ = this.store.select(quoteSelector).subscribe((s) => {
      this.quoteRequestType = QuoteRequestType[s.quote?.quoteRequestState?.quoteRequestType?.trim()];
      this.quoteState = s?.quote?.quoteState;
      this.quote = s?.quote;
      this.user = s?.userViewingQuote;
      if (this.quoteRequestType) {
        this.pricingSourceService.getPricingSource(this.quoteRequestType, this.quoteRequestState?.commodity).subscribe((ps) => {
        this.pricingSource = ps.source;
        });
      }
    });

    this.uhConfig$ = this.store.select(configurationSelector).subscribe((config) => {
      this.docuSignEnabled = true; // config.supplierConfiguration['docuSignLogin']?.length > 0; Let's not put creds in the client side
      this.supplierId = config.supplierConfiguration.Id;
    });
    this.routeSubscription$ = this.route.params.subscribe((x) => {
      this.quoteRequestId = x.quoteRequestId;
      if (!isNaN(x.quoteRequestId)) {
        this.quoteRequestId = Number(x.quoteRequestId);
      }
    });

    this.productType$ = this.store.select(productTypeSelector).subscribe((pt) => {
      this.productType = pt;
    });

    this.isReadOnly$ = this.store.select(isReadOnlySelector).subscribe((isReadOnly) => {
      this.isReadOnly = isReadOnly;
    });
  }

  public styles = (quoteState: QuoteState): { class: string; icon: string; disabled: boolean } => {
    let style: { class: string; icon: string; disabled: boolean } = { class: '', icon: '', disabled: false };
    switch (quoteState) {
      case QuoteState.APPROVAL_ACCEPTED:
        style =
          this.offer.approvalStatus === ApprovalStatus.Approved
            ? { class: 'accepted', icon: 'check_circle', disabled: true }
            : { class: 'rejected', icon: 'cancel', disabled: true };
        break;
      case QuoteState.OFFER_ACCEPTED && QuoteState.PENDING_ENROLLMENT:
      case QuoteState.OFFER_ACCEPTED:
        {
          style =
            this.offer.status === QuotePriceStatus.OfferAccepted &&
            ( this.offer.approvalStatus === ApprovalStatus.Approved || 
              this.offer.approvalStatus === ApprovalStatus.NotRequired || 
              this.offer.approvalStatus === ApprovalStatus.UnderManagerReview)
              ? { class: 'accepted', icon: 'check_circle', disabled: true }
              : { class: 'rejected', icon: 'cancel', disabled: true };
        }
        break;
      case QuoteState.APPROVAL_REJECTED:
      case QuoteState.OFFER_REJECTED:
        style = { class: 'rejected', icon: 'cancel', disabled: true };
        break;
      case QuoteState.APPROVAL_REQUIRED:
        {
          style =
            this.offer.status === QuotePriceStatus.Generated ||
              this.offer.status === QuotePriceStatus.UnderReview
              ? { class: 'accepted', icon: 'check_circle', disabled: false }
              : { class: 'rejected', icon: 'cancel', disabled: true };
        }
        break;
      case QuoteState.GENERATED:
        {
          style =
            this.offer.status === QuotePriceStatus.Generated
              ? { class: 'accepted', icon: 'check_circle', disabled: false }
              : { class: 'rejected', icon: 'cancel', disabled: true };
        }
        break;
      default:
        style = { class: '', icon: '', disabled: false };
    }

    if (this.isAnyMarginEdited) {
      style.class = style.class + 'edited';
      return style;
    }
    return style;
  };

  getOfferGridClass() {
    let classes = {};

    classes[this.styles(this.quoteState).class] = true;

    if (this.quote?.quoteRequestState?.quoteRequestType.toString().replace(' ', '') === QuoteRequestType.QuickQuote) {
      classes['offer-grid-5'] = true;
    } else {
      classes['offer-grid-7'] = true;
    }

    return classes;
  }

  public progressOffer() {

    if (this.offer.status === QuotePriceStatus.OfferRejected) 
    return;

    if (this.quoteState === QuoteState.APPROVAL_REQUIRED && 
        this.quote?.prices?.find(p => p.approvalStatus === ApprovalStatus.UnderManagerReview) != undefined &&  
        this.quote?.prices?.find(p => p.approvalStatus === ApprovalStatus.UnderManagerReview) != null)
    {
      if (!this.user.isSupplierManagerOrSalesManager) {
        this.openManagerApprovalRequiredDialog();
      } else {
        this.quoteRequestService.acceptOffer(this.quoteRequestId, this.offer.term, this.offer.planType).subscribe((x) => {
          LogRocket.track('offer_accepted', { term: this.offer.term, totalCost: this.offer.totalCost, rate: this.offer.sellingPrice });
          this.actioned.emit(this.offer.quotePriceId);
          window.location.reload();
        });
      }
    }

    if (this.quoteState === QuoteState.APPROVAL_REQUIRED && 
        this.quote?.prices?.find(p => p.approvalStatus === ApprovalStatus.UnderManagerReview) === undefined)
    {
        this.openApprovalRequiredDialog();
    }

    if (this.quoteState === QuoteState.REQUEST_APPROVED || this.quoteState === QuoteState.GENERATED) {
      this.quoteRequestService.acceptOffer(this.quoteRequestId, this.offer.term, this.offer.planType).subscribe((x) => {
        LogRocket.track('offer_accepted', { term: this.offer.term, totalCost: this.offer.totalCost, rate: this.offer.sellingPrice });
        this.actioned.emit(this.offer.quotePriceId);
      });
    }
  }

  public openManagerApprovalRequiredDialog() {

    const sendOfferDialog = this.dialog.open(QuoteExpirationDisclaimerDialogComponent, {
      width: '40%',
      data: {
        title: 'Quote Expiration Disclaimer',
        quoteRequestId: this.offer.quotePriceId,
        approvedTerms: [this.offer.term],
      },
    });

    this.sendOfferDialog$ = sendOfferDialog.afterClosed().subscribe((result) => {
      if (result.success) {
        this.store.dispatch(new OfferApprovalRequest([this.offer]));
        this.quoteRequestService.requestExpiredQuoteApproval(this.quoteRequestId, this.offer.term, this.offer.planType).subscribe((x) => {
          LogRocket.track('offer_request', { term: this.offer.term, totalCost: this.offer.totalCost, rate: this.offer.sellingPrice });
          window.location.reload();
        });
      }
    });

  }

  public openApprovalRequiredDialog() {

    // Supplier Manager - QuoteRequested state -> Instantiate send offer dialog
    const sendOfferDialog = this.dialog.open(ResolveOffersDialogComponent, {
      width: '40%',
      data: {
        title: 'Send Offer',
        quoteRequestId: this.offer.quotePriceId,
        approvedTerms: [this.offer.term],
      },
    });

    this.sendOfferDialog$ = sendOfferDialog.afterClosed().subscribe((result) => {
      if (result.success) {
        this.sendOffer();
      }
    });

  }

  public setQuotePriceResult() {
    if (this.offer.approvalStatus === ApprovalStatus.Rejected || this.offer.status === QuotePriceStatus.OfferRejected) {
      return 'Offer Rejected';
    }
    if (this.offer.status === QuotePriceStatus.OfferAccepted) {
      return 'Offer Accepted';
    }
  }

  public showMoreDetails(): void {
    this.dialog.open(MoreDetailsComponent, {
      width: '80%',
      height: '60%',
      data: {
        title: 'Get Quote - More Details',
        maxCharacters: 255,
        quoteRequestState: this.quote.quoteRequestState,
        offer: this.offer,
        term: this.offer.term,
        startDate: this.offer.startDate,
        consumptionData: this.consumptionData,
        uplift: this.quote.upliftDollar,
        quoteRequestId: this.quoteRequestId,
      },
    });
  }

  marginPerTermChanges(marginElement: HTMLSelectElement, offer: Offer, isFocusedMarginEdited: boolean) {
    let marginCustom = Number(marginElement?.value);
    if (marginElement?.value.trim() == "")
    {
      marginCustom = this.marginPreviousInput;
      marginElement.value = String(this.marginPreviousInput);
    }
    if (marginCustom != Number(this.marginPreviousInput)) {
    if (isNaN(marginCustom)) {
      isFocusedMarginEdited = false;
      marginCustom = offer.margin;
    }
    marginElement.value = marginCustom.toString();
      this.isAnyMarginEdited = marginCustom != Number(offer?.margin) || !!this.isAllMarginChanged;
    this.setMarginTermDynamic(offer.term, marginCustom);
    }
  }

  private setMarginTermDynamic(term: number, newMargin: number) {
    this.marginTerm$
      .subscribe((x) => {
        let currentMarginTermValues = [...x];
        let marginTermResult = currentMarginTermValues.find((m) => m.term === term);
        if (!marginTermResult) {
          // Term not found — add a new entry rather than corrupting the array
          currentMarginTermValues.push({ term, margin: newMargin, isSweetSpot: false });
        } else {
          const index = currentMarginTermValues.indexOf(marginTermResult);
          currentMarginTermValues.splice(index, 1);
          currentMarginTermValues.push({ ...marginTermResult, margin: newMargin });
        }
        this.marginTermResults = currentMarginTermValues;
      })
      .unsubscribe();
    this.store.dispatch(new SetMarginTerm(this.quote?.quoteRequestState?.quoteRequestId.toString(), this.marginTermResults));
  }

  getInitialMarginPerTerm(): MarginTerm[] {
    
    let marginTerm = Array<MarginTerm>();
    let marginTermResp = Array<MarginTerm>();
    this.contractLengths$ = this.store
      .select(contractLengths_Selected)
      .pipe(
        take(1),
        map((x) => {
          marginTerm = [];
          x.forEach((item) => {
            const { months } = item;
            marginTerm.push({ term: months, margin: 0 as number, isSweetSpot: false });
          });
        }),
        map(() => {
          return marginTerm as MarginTerm[];
        })
      )
      .subscribe((mt) => {
        marginTermResp = mt;
      })
      .unsubscribe();
    return marginTermResp;
  }

  private sendOffer() {
    this.store.dispatch(new ApprovalAccept([this.offer]));
    this.actioned.emit(this.offer.quotePriceId);
  }

  public getActionLabel() {
    
    if (this.offer.status === QuotePriceStatus.OfferAccepted && this.quoteState === QuoteState.OFFER_ACCEPTED) {
      return 'Offer Accepted';
    }
    if (
      this.offer.status === QuotePriceStatus.OfferRejected &&
      (this.quoteState === QuoteState.OFFER_REJECTED || this.quoteState === QuoteState.OFFER_ACCEPTED || this.quoteState === QuoteState.APPROVAL_REQUIRED)
    ) {
      return 'Offer Rejected';
    }
    if (this.quoteState === QuoteState.GENERATED || this.quoteState === QuoteState.REQUEST_APPROVED) { 
      return 'Accept Offer';
    }
    if (this.offer.approvalStatus === ApprovalStatus.Approved && this.quoteState === QuoteState.APPROVAL_ACCEPTED) {
      return 'Offer Sent';
    }
    if (
      this.offer.approvalStatus === ApprovalStatus.Rejected &&
      (this.quoteState === QuoteState.APPROVAL_REJECTED || this.quoteState === QuoteState.APPROVAL_ACCEPTED)
    ) {
      return 'Offer Rejected';
    } 
    if (this.quoteState === QuoteState.APPROVAL_REQUIRED) {
      return 'Send Offer';
    }  
  }

  public isSupplierManager() {
    return this.user?.currentOrganisation?.role === UserRole.SupplierManager;
  }

  displayStatusMessage() : string {
    if (!this.offer.sellingPrice && !this.offer.statusMessage) {
      return 'Price for this term is not available';
    } else if (!this.offer.sellingPrice ) {
      return this.offer.statusMessage;
    }
  }
  
  getAgentFeeTotal() {    
    const agentFeeTotal = this.offer.totalConsumption * (this.offer.agentFee / 1000);
    return agentFeeTotal;
  }
}
